import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation, Trans } from 'react-i18next';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditContext';
import {
  DEFAULT_CREDIT_ALERT_CONFIG,
  DEFAULT_DEGRADATION_CONFIG,
  NOTIFICATION_REGISTRY,
  supportsNonEmailChannels,
  type DegradationScope,
  type EmailNotificationType,
  type NotificationCadence,
  type NotificationTypeConfig,
  type TenantNotificationConfigView,
  type User,
} from '@traaker/shared';
import slackLogo from '../../assets/channels/slack.svg';
import gmailLogo from '../../assets/channels/gmail.svg';
import webhookLogo from '../../assets/channels/webhook.svg';

const CADENCE_LABEL_KEY: Record<NotificationCadence, string> = {
  daily: 'notificationsSettings.cadence.daily',
  weekly: 'notificationsSettings.cadence.weekly',
  monthly: 'notificationsSettings.cadence.monthly',
};

const TYPE_LABEL_KEY: Record<EmailNotificationType, string> = {
  global_report: 'notificationsSettings.type.globalReport',
  score_report: 'notificationsSettings.type.scoreReport',
  competitor_news: 'notificationsSettings.type.competitorNews',
  score_degradation: 'notificationsSettings.type.scoreDegradation',
  tasks_recos: 'notificationsSettings.type.tasksRecos',
  traaker_news: 'notificationsSettings.type.traakerNews',
  geo_news: 'notificationsSettings.type.geoNews',
  trial_lifecycle: 'notificationsSettings.type.trialLifecycle',
  credit_alert: 'notificationsSettings.type.creditAlert',
  audit_failed: 'notificationsSettings.type.auditFailed',
};

const TYPE_DESCRIPTION_KEY: Record<EmailNotificationType, string> = {
  global_report: 'notificationsSettings.type.globalReportInfo',
  score_report: 'notificationsSettings.type.scoreReportInfo',
  competitor_news: 'notificationsSettings.type.competitorNewsInfo',
  score_degradation: 'notificationsSettings.type.scoreDegradationInfo',
  tasks_recos: 'notificationsSettings.type.tasksRecosInfo',
  traaker_news: 'notificationsSettings.type.traakerNewsInfo',
  geo_news: 'notificationsSettings.type.geoNewsInfo',
  trial_lifecycle: 'notificationsSettings.type.trialLifecycleInfo',
  credit_alert: 'notificationsSettings.type.creditAlertInfo',
  audit_failed: 'notificationsSettings.type.auditFailedInfo',
};

const DEGRADATION_SCOPE_LABEL_KEY: Record<DegradationScope, string> = {
  website_overall: 'notificationsSettings.degradationScope.websiteOverall',
  bp_overall: 'notificationsSettings.degradationScope.bpOverall',
  bp_axis: 'notificationsSettings.degradationScope.bpAxis',
  bp_prompt: 'notificationsSettings.degradationScope.bpPrompt',
  earned_media_overall: 'notificationsSettings.degradationScope.earnedMediaOverall',
  product_catalog_overall: 'notificationsSettings.degradationScope.productCatalogOverall',
  geo_visibility_measure: 'notificationsSettings.degradationScope.geoVisibilityMeasure',
};

// Card list is derived from the registry's own visibleInSettings field —
// NOT a hand-written list — so hiding a type (e.g. traaker_news/geo_news
// until broadcasts ship, trial_lifecycle always) only ever needs a change in
// one place: the registry itself.
const VISIBLE_TYPES = (Object.keys(NOTIFICATION_REGISTRY) as EmailNotificationType[]).filter(
  (type) => NOTIFICATION_REGISTRY[type].visibleInSettings,
);

interface ConfigResponse {
  config: TenantNotificationConfigView;
}

export default function NotificationsSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [config, setConfig] = useState<TenantNotificationConfigView | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  const canEdit = user?.role !== 'viewer';
  // Only agency is credit-metered server-side (CreditService.charge gates on
  // `tier === 'agency'` alone) — `useCredits().isAgency` is the idiomatic
  // client-side mirror of that, used the same way across the app (BillingSettings,
  // CreditBalanceChip, etc.), rather than a per-call-site tier literal.
  const { isAgency: creditMetered } = useCredits();

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get<ConfigResponse>('/email-notifications/config'), api.get<User[]>('/users')])
      .then(([cfg, u]) => {
        if (cancelled) return;
        setConfig(cfg.config);
        setUsers(u);
      })
      .catch(() => {
        if (!cancelled) setConfig({ types: {} });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleUpdate = async (type: EmailNotificationType, patch: Partial<NotificationTypeConfig>) => {
    try {
      const res = await api.put<{ config: TenantNotificationConfigView }>(`/email-notifications/config/${type}`, patch);
      setConfig(res.config);
    } catch {
      setMessage(t('notificationsSettings.toast.updateError'));
    }
  };

  if (loading || !config) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {message && (
        <Alert severity="info" onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      <SlackCard
        slack={config.slack}
        canEdit={canEdit}
        onChange={setConfig}
        onMessage={setMessage}
      />
      {VISIBLE_TYPES.filter((type) => type !== 'credit_alert' || creditMetered).map((type) => (
        <NotificationTypeCard
          key={type}
          type={type}
          typeConfig={config.types[type]}
          users={users}
          canEdit={canEdit}
          slackConnected={!!config.slack}
          onUpdate={(patch) => handleUpdate(type, patch)}
        />
      ))}
    </Box>
  );
}

/**
 * Slack integration (plan §13.2), collapsed by default — the connect/test/
 * disconnect controls are one-time setup, not something worth taking up
 * permanent vertical space on a settings page most tenants never revisit.
 * The URL is write-only from the client's point of view: the API returns a
 * `urlHint` (domain + last 4) and never the URL, so there is nothing to
 * prefill — "replace" means pasting a new one.
 */
function SlackCard({
  slack,
  canEdit,
  onChange,
  onMessage,
}: {
  slack: TenantNotificationConfigView['slack'];
  canEdit: boolean;
  onChange: (config: TenantNotificationConfigView) => void;
  onMessage: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put<{ config: TenantNotificationConfigView }>('/email-notifications/slack', {
        webhookUrl: url.trim(),
      });
      onChange(res.config);
      setUrl('');
      onMessage(t('notificationsSettings.slack.saved'));
    } catch {
      onMessage(t('notificationsSettings.slack.invalidUrl'));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      const res = await api.delete<{ config: TenantNotificationConfigView }>('/email-notifications/slack');
      onChange(res.config);
      onMessage(t('notificationsSettings.slack.disconnected'));
    } catch {
      onMessage(t('notificationsSettings.toast.updateError'));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const res = await api.post<{ ok: boolean; responseCode?: number }>('/email-notifications/slack/test', {});
      onMessage(
        res.ok
          ? t('notificationsSettings.slack.testOk')
          : t('notificationsSettings.slack.testFailed', { code: res.responseCode ?? '—' }),
      );
      if (res.ok && slack?.broken) {
        onChange((await api.get<ConfigResponse>('/email-notifications/config')).config);
      }
    } catch {
      onMessage(t('notificationsSettings.toast.updateError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
          onClick={() => setOpen((o) => !o)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={slackLogo} alt="" width={22} height={22} style={{ display: 'block' }} />
            <Typography variant="h6" fontWeight={600}>
              {t('notificationsSettings.slack.title')}
            </Typography>
            {slack && <Chip label={slack.urlHint} size="small" />}
            {slack?.broken && <Chip label={t('notificationsSettings.slack.brokenChip')} size="small" color="warning" />}
          </Box>
          <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
        </Box>

        <Collapse in={open}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <Trans
                i18nKey="notificationsSettings.slack.description"
                components={{ a: <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noopener noreferrer" /> }}
              />
            </Typography>

            {slack?.broken && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('notificationsSettings.slack.brokenWarning')}
              </Alert>
            )}

            {slack ? (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button size="small" disabled={busy} onClick={sendTest}>
                  {t('notificationsSettings.slack.sendTest')}
                </Button>
                {canEdit && (
                  <Button size="small" color="error" disabled={busy} onClick={disconnect}>
                    {t('notificationsSettings.slack.disconnect')}
                  </Button>
                )}
              </Box>
            ) : null}

            {canEdit && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('notificationsSettings.slack.urlLabel')}
                  placeholder="https://hooks.slack.com/services/..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  sx={{ maxWidth: 460 }}
                />
                <Button variant="contained" size="small" disabled={busy || !url.trim()} onClick={save} sx={{ mt: 0.25 }}>
                  {slack ? t('notificationsSettings.slack.replace') : t('notificationsSettings.slack.connect')}
                </Button>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

function NotificationTypeCard({
  type,
  typeConfig,
  users,
  canEdit,
  slackConnected,
  onUpdate,
}: {
  type: EmailNotificationType;
  typeConfig: NotificationTypeConfig | undefined;
  users: User[];
  canEdit: boolean;
  slackConnected: boolean;
  onUpdate: (patch: Partial<NotificationTypeConfig>) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const def = NOTIFICATION_REGISTRY[type];
  const enabled = typeConfig?.enabled ?? def.defaultEnabled;
  const cadence = typeConfig?.cadence ?? def.defaultCadence;
  const recipients = typeConfig?.recipients ?? [];
  const channels = typeConfig?.channels ?? {};
  const validatedUsers = users.filter((u) => !u.cognitoSub.startsWith('pending:'));
  const hasConfigPanel = type === 'score_degradation' || type === 'credit_alert';

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', flex: 1, minWidth: 0 }}
            onClick={() => setOpen((o) => !o)}
          >
            <Typography variant="h6" fontWeight={600} sx={{ mr: 0.5 }}>
              {t(TYPE_LABEL_KEY[type])}
            </Typography>
            <Tooltip title={t(TYPE_DESCRIPTION_KEY[type])} arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: 17, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          </Box>

          {def.cadences && (
            <Select
              size="small"
              value={cadence || def.defaultCadence}
              disabled={!canEdit}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onUpdate({ cadence: e.target.value as NotificationCadence })}
              sx={{ minWidth: 150 }}
            >
              {def.cadences.map((c) => (
                <MenuItem key={c} value={c}>
                  {t(CADENCE_LABEL_KEY[c])}
                </MenuItem>
              ))}
            </Select>
          )}

          <FormControlLabel
            sx={{ m: 0 }}
            onClick={(e) => e.stopPropagation()}
            control={
              <Checkbox
                checked={enabled}
                disabled={!canEdit}
                onChange={(e) => onUpdate({ enabled: e.target.checked, cadence })}
              />
            }
            label={t('notificationsSettings.enabled')}
          />

          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={open}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <ChannelSection
              icon={gmailLogo}
              title={t('notificationsSettings.channelSection.email')}
            >
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                {recipients.map((r) => {
                  const u = validatedUsers.find((v) => v.userId === r.userId);
                  return (
                    <Chip
                      key={r.userId}
                      label={u ? `${u.name} (${u.email})` : r.userId}
                      size="small"
                      onDelete={
                        canEdit
                          ? () => onUpdate({ recipients: recipients.filter((x) => x.userId !== r.userId) })
                          : undefined
                      }
                    />
                  );
                })}
                {recipients.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {t('notificationsSettings.noRecipient')}
                  </Typography>
                )}
              </Box>
              {canEdit && (
                <Select
                  size="small"
                  displayEmpty
                  value=""
                  onChange={(e) => {
                    const userId = e.target.value as string;
                    if (!userId) return;
                    onUpdate({ recipients: [...recipients, { userId }] });
                  }}
                  sx={{ mt: 1, minWidth: 220 }}
                >
                  <MenuItem value="" disabled>
                    {t('notificationsSettings.addRecipient')}
                  </MenuItem>
                  {validatedUsers
                    .filter((u) => !recipients.some((r) => r.userId === u.userId))
                    .map((u) => (
                      <MenuItem key={u.userId} value={u.userId}>
                        {u.name} ({u.email})
                      </MenuItem>
                    ))}
                </Select>
              )}
            </ChannelSection>

            {supportsNonEmailChannels(type) && (
              <>
                <ChannelSection icon={slackLogo} title={t('notificationsSettings.channelSection.slack')}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!channels.slack}
                        disabled={!canEdit || !slackConnected}
                        onChange={(e) => onUpdate({ channels: { ...channels, slack: e.target.checked } })}
                      />
                    }
                    label={
                      slackConnected
                        ? t('notificationsSettings.channels.slack')
                        : t('notificationsSettings.channels.slackNotConnected')
                    }
                  />
                </ChannelSection>

                <ChannelSection icon={webhookLogo} title={t('notificationsSettings.channelSection.webhook')}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        // Webhooks default ON: a tenant that subscribed an endpoint to
                        // `notification.<type>` in the Integrations tab already opted
                        // in there — this box is the per-type mute, not the opt-in.
                        checked={channels.webhook !== false}
                        disabled={!canEdit}
                        onChange={(e) => onUpdate({ channels: { ...channels, webhook: e.target.checked } })}
                      />
                    }
                    label={t('notificationsSettings.channels.webhook')}
                  />
                </ChannelSection>
              </>
            )}

            {hasConfigPanel && (
              <ConfigSection title={t('notificationsSettings.configSection.title')}>
                {type === 'score_degradation' && (
                  <DegradationConfig
                    degradation={typeConfig?.degradation}
                    canEdit={canEdit}
                    onChange={(degradation) => onUpdate({ degradation })}
                  />
                )}
                {type === 'credit_alert' && (
                  <CreditAlertConfig
                    creditAlert={typeConfig?.creditAlert}
                    canEdit={canEdit}
                    onChange={(creditAlert) => onUpdate({ creditAlert })}
                  />
                )}
              </ConfigSection>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

/** One labelled sub-section of the recipients/channels dropdown — email, Slack, or webhooks. */
function ChannelSection({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <img src={icon} alt="" width={16} height={16} style={{ display: 'block' }} />
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

function ConfigSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

const ALL_DEGRADATION_SCOPES: DegradationScope[] = [
  'website_overall',
  'bp_overall',
  'bp_axis',
  'bp_prompt',
  'earned_media_overall',
  'product_catalog_overall',
  'geo_visibility_measure',
];

function DegradationConfig({
  degradation,
  canEdit,
  onChange,
}: {
  degradation: NotificationTypeConfig['degradation'];
  canEdit: boolean;
  onChange: (degradation: NotificationTypeConfig['degradation']) => void;
}) {
  const { t } = useTranslation();
  const thresholdPoints = degradation?.thresholdPoints ?? DEFAULT_DEGRADATION_CONFIG.thresholdPoints;
  const consecutiveRuns = degradation?.consecutiveRuns ?? DEFAULT_DEGRADATION_CONFIG.consecutiveRuns;
  const scopes = degradation?.scopes ?? DEFAULT_DEGRADATION_CONFIG.scopes;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <TextField
          type="number"
          size="small"
          label={t('notificationsSettings.degradation.thresholdPoints')}
          value={thresholdPoints}
          disabled={!canEdit}
          onChange={(e) => onChange({ thresholdPoints: Number(e.target.value), consecutiveRuns, scopes })}
          sx={{ width: 180 }}
        />
        <TextField
          type="number"
          size="small"
          label={t('notificationsSettings.degradation.consecutiveRuns')}
          value={consecutiveRuns}
          disabled={!canEdit}
          onChange={(e) => onChange({ thresholdPoints, consecutiveRuns: Number(e.target.value), scopes })}
          sx={{ width: 180 }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {ALL_DEGRADATION_SCOPES.map((scope) => (
          <FormControlLabel
            key={scope}
            control={
              <Checkbox
                size="small"
                checked={scopes.includes(scope)}
                disabled={!canEdit}
                onChange={() =>
                  onChange({
                    thresholdPoints,
                    consecutiveRuns,
                    scopes: scopes.includes(scope) ? scopes.filter((s) => s !== scope) : [...scopes, scope],
                  })
                }
              />
            }
            label={t(DEGRADATION_SCOPE_LABEL_KEY[scope])}
          />
        ))}
      </Box>
    </Box>
  );
}

function CreditAlertConfig({
  creditAlert,
  canEdit,
  onChange,
}: {
  creditAlert: NotificationTypeConfig['creditAlert'];
  canEdit: boolean;
  onChange: (creditAlert: NotificationTypeConfig['creditAlert']) => void;
}) {
  const { t } = useTranslation();
  const thresholdCredits = creditAlert?.thresholdCredits ?? DEFAULT_CREDIT_ALERT_CONFIG.thresholdCredits;

  return (
    <TextField
      type="number"
      size="small"
      label={t('notificationsSettings.creditAlert.threshold')}
      value={thresholdCredits}
      disabled={!canEdit}
      onChange={(e) => onChange({ thresholdCredits: Number(e.target.value) })}
      sx={{ width: 220 }}
    />
  );
}
